import { ChangeEvent, forwardRef } from 'react'
import { InputTextProps } from './Input_Text'
import { Input, TextInputProps } from '@mantine/core'
import { Controller, useFormContext } from 'react-hook-form'

type InputBProps = {
  length?: number
  mask?: string
  separator?: string
  formatChars?: Record<string, string>
  maskChar?: string | null
}

const getAllowedPattern = (formatChars: Record<string, string>, mask: string) =>
  new RegExp(formatChars[mask[0]] ?? '[0-9a-fA-F]')

const formatBinaryValue = ({
  value,
  length = 16,
  mask = 'hh',
  separator = ' ',
  formatChars = { h: '[0-9a-fA-F]' },
}: Pick<InputBProps, 'length' | 'mask' | 'separator' | 'formatChars'> & {
  value: string
}) => {
  const allowedPattern = getAllowedPattern(formatChars, mask)
  const groupSize = mask.length || 2
  const normalized = Array.from(value)
    .filter(char => char !== separator && allowedPattern.test(char))
    .join('')
    .slice(0, length)
    .toUpperCase()

  return normalized.match(new RegExp(`.{1,${groupSize}}`, 'g'))?.join(separator)
}

const normalizeBinaryValue = (value: unknown) =>
  typeof value === 'string' ? value.toUpperCase() : undefined

export const InputBinaryPure = forwardRef<
  HTMLInputElement,
  Omit<TextInputProps, 'value' | 'ref' | 'icon'> & {
    value?: string
  } & InputBProps
>(
  (
    {
      wrapperProps,
      required,
      label,
      error,
      onChange,
      value,
      leftSection,
      length,
      mask,
      separator,
      formatChars,
      placeholder,
      maskChar,
      description,
      ...props
    },
    ref,
  ) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const formattedValue =
        formatBinaryValue({
          value: event.currentTarget.value,
          length,
          mask,
          separator,
          formatChars,
        }) ?? ''

      onChange?.(formattedValue as never)
    }

    return (
      <Input.Wrapper
        required={required}
        label={label}
        error={error}
        description={description}
        {...(wrapperProps as Record<string, unknown>)}
      >
        <Input
          data-invalid={!!error}
          ref={ref}
          onChange={handleChange}
          value={normalizeBinaryValue(value)}
          leftSection={leftSection}
          placeholder={placeholder}
          {...(props as Record<string, unknown>)}
        />
      </Input.Wrapper>
    )
  },
)

InputBinaryPure.displayName = 'TextInputBinaryPure'

export const InputBinary = ({
  name,
  icon: Icon,
  className,
  length,
  ...props
}: InputTextProps & InputBProps) => {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState }) => {
        const { error } = fieldState
        return (
          <InputBinaryPure
            {...field}
            {...props}
            length={length}
            ref={ref}
            className={className}
            error={error?.message}
            leftSection={Icon ? <Icon size={'1.2em'} /> : null}
          />
        )
      }}
    />
  )
}
